import { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import {
  Container,
  Box,
  Typography,
  IconButton,
  Chip,
  Snackbar,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Checkbox from '@mui/material/Checkbox';
import { PersonAddAlt } from '@mui/icons-material';
import { format, startOfWeek, addDays, isSameDay, startOfMonth } from 'date-fns';
import { cs } from 'date-fns/locale';

import AuthContext from '../contexts/AuthContext';
import { useAxios } from '../hooks/useAxios';
import MobileWeekChrome, { jumpWeekToDay } from './MobileWeekChrome';
import { getIsoDate, getLocalDate } from '../utils/dateHandler';

const dialogPaperProps = {
  className: 'glass-surface-accent glass-modal',
  sx: { borderRadius: '16px' },
};
const dialogBackdropProps = {
  sx: { backgroundColor: 'rgba(6, 6, 8, 0.65)', backdropFilter: 'blur(8px)' },
};

const ShiftAllocations = () => {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [activeDay, setActiveDay] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [allocationsByDay, setAllocationsByDay] = useState({});
  const [hasAccess, setHasAccess] = useState(true);
  const statusFilter = 'all';
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [assignAllocation, setAssignAllocation] = useState(null);
  const [assignDetails, setAssignDetails] = useState(null);
  const [assignEmployees, setAssignEmployees] = useState([]);
  const [assignSelectedIds, setAssignSelectedIds] = useState([]);
  const [isLoadingAssignEmployees, setIsLoadingAssignEmployees] = useState(false);
  const [assignSnackbar, setAssignSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  });

  const { token, appSettings, featurePermissions, isAdmin } = useContext(AuthContext);
  const { requestStatus, sendRequest, clearRequestStatus } = useAxios();

  const outletId = appSettings?.outletId || null;
  const shiftAllocationsPermissions = useMemo(
    () => featurePermissions?.find((x) => x.feature === 'shiftAllocations')?.permissions,
    [featurePermissions]
  );
  const canReadShiftAllocations = useMemo(
    () => isAdmin || shiftAllocationsPermissions?.canReadAny,
    [isAdmin, shiftAllocationsPermissions]
  );
  const canUpdateShiftAllocations = useMemo(
    () => isAdmin || shiftAllocationsPermissions?.canUpdateAny,
    [isAdmin, shiftAllocationsPermissions]
  );

  const loadShiftAllocations = useCallback(async () => {
    if (!token || !outletId || !canReadShiftAllocations) return;
    try {
      const response = await sendRequest(
        '/shiftAllocations/week',
        'POST',
        {
          outletId,
          weekStart: format(weekStart, 'yyyy-MM-dd'),
        },
        { Authorization: `Bearer ${token}` }
      );
      const itemsByDay = response?.shiftAllocations || {};
      Object.values(itemsByDay).forEach((dayItems) => {
        dayItems.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
      });
      setAllocationsByDay(itemsByDay);
      setHasAccess(true);
    } catch (error) {
      if (error?.response?.status === 403) {
        setHasAccess(false);
      }
      setAllocationsByDay({});
    }
  }, [token, outletId, canReadShiftAllocations, weekStart, sendRequest]);

  useEffect(() => {
    loadShiftAllocations();
  }, [loadShiftAllocations]);

  const handlePrevWeek = () => {
    const next = addDays(weekStart, -7);
    setWeekStart(next);
    setActiveDay(next);
  };

  const handleNextWeek = () => {
    const next = addDays(weekStart, 7);
    setWeekStart(next);
    setActiveDay(next);
  };

  const handleJumpToDay = useCallback((day) => {
    jumpWeekToDay(day, setWeekStart, setActiveDay);
  }, []);

  const visibleMonth = useMemo(() => startOfMonth(weekStart), [weekStart]);

  const weekDayKeys = useMemo(
    () =>
      new Set(
        Array.from({ length: 7 }, (_, index) => format(addDays(weekStart, index), 'yyyy-MM-dd'))
      ),
    [weekStart]
  );

  const openAllocationsForCalendar = useMemo(() => {
    const result = {};
    Object.entries(allocationsByDay).forEach(([dayKey, items]) => {
      if (items?.length) {
        result[dayKey] = items;
      }
    });
    return result;
  }, [allocationsByDay]);

  const getEmployeeRowColor = (employee) => {
    const palette = {
      booked: 'rgba(76, 175, 80, 0.18)',
      perfect: 'rgba(76, 175, 80, 0.18)',
      overlap: 'rgba(255, 193, 7, 0.18)',
      outOfRange: 'rgba(244, 67, 54, 0.18)',
      noAvailability: 'rgba(158, 158, 158, 0.18)',
    };
    if (employee?.isBooked) return palette.booked;
    return palette[employee?.status] || 'transparent';
  };

  const openAssignDialog = async (allocation) => {
    if (!allocation || !canUpdateShiftAllocations) return;
    setIsAssignDialogOpen(true);
    setIsLoadingAssignEmployees(true);
    setAssignAllocation(allocation);
    setAssignEmployees([]);
    setAssignSelectedIds(allocation.employee?._id ? [allocation.employee._id] : []);

    try {
      const [detailsResponse, employeesResponse] = await Promise.all([
        sendRequest(`/shiftAllocations/${allocation.id || allocation._id}`, 'GET', null, {
          Authorization: `Bearer ${token}`,
        }),
        sendRequest(`/shiftAllocations/${allocation.id || allocation._id}/employees`, 'GET', null, {
          Authorization: `Bearer ${token}`,
        }),
      ]);
      const details = detailsResponse?.shiftAllocation;
      if (details) {
        setAssignDetails({
          name: details.name || '',
          shiftScopeId: details.shiftScope?._id || details.shiftScope?.id || '',
          date: new Date(details.date),
          startTime: details.startTime ? new Date(details.startTime) : null,
          endTime: details.endTime ? new Date(details.endTime) : null,
          capacity: details.capacity || 1,
          colorCode: details.colorCode || '',
          bookedColorCode: details.bookedColorCode || '',
          notes: details.notes || '',
        });
      }
      const employees = employeesResponse?.employees || [];
      setAssignEmployees(employees);
      if (details?.employee?._id) {
        setAssignSelectedIds([details.employee._id]);
      }
    } catch (error) {
      setAssignEmployees([]);
    } finally {
      setIsLoadingAssignEmployees(false);
    }
  };

  const closeAssignDialog = () => {
    setIsAssignDialogOpen(false);
    setAssignAllocation(null);
    setAssignDetails(null);
    setAssignEmployees([]);
    setAssignSelectedIds([]);
  };

  const confirmAssignEmployee = async () => {
    if (!assignAllocation || !assignDetails) return;
    await sendRequest(
      `/shiftAllocations/${assignAllocation.id || assignAllocation._id}`,
      'PATCH',
      {
        name: assignDetails.name,
        shiftScopeId: assignDetails.shiftScopeId,
        date: getLocalDate(assignDetails.date),
        startTime: getLocalDate(assignDetails.startTime),
        endTime: getLocalDate(assignDetails.endTime),
        capacity: Number(assignDetails.capacity || 1),
        colorCode: assignDetails.colorCode,
        bookedColorCode: assignDetails.bookedColorCode,
        notes: assignDetails.notes,
        bookedEmployees: assignSelectedIds,
      },
      { Authorization: `Bearer ${token}` }
    );
    closeAssignDialog();
    loadShiftAllocations();
  };

  const handleAssignToggle = (employeeId) => {
    if (!assignDetails) return;
    const capacity = Number(assignDetails.capacity || 1);
    const isSelected = assignSelectedIds.includes(employeeId);

    if (capacity === 1) {
      setAssignSelectedIds(isSelected ? [] : [employeeId]);
      return;
    }

    if (isSelected) {
      setAssignSelectedIds((prev) => prev.filter((id) => id !== employeeId));
      return;
    }

    if (assignSelectedIds.length < capacity) {
      setAssignSelectedIds((prev) => [...prev, employeeId]);
      return;
    }

    setAssignSnackbar({
      open: true,
      message: 'Kapacita je již naplněna',
      severity: 'warning',
    });
  };

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  const filteredAllocationsByDay = useMemo(() => {
    const result = {};
    weekDays.forEach((day) => {
      const key = format(day, 'yyyy-MM-dd');
      const dayItems = allocationsByDay[key] || [];
      let items = [...dayItems];
      if (statusFilter === 'booked') {
        items = items.filter((item) => item.isBooked);
      } else if (statusFilter === 'free') {
        items = items.filter((item) => !item.isBooked);
      }
      result[key] = items;
    });
    return result;
  }, [allocationsByDay, weekDays, statusFilter]);

  const renderContent = () => {
    if (!canReadShiftAllocations || !hasAccess) {
      return <Alert severity="info">Nemáte oprávnění pro zobrazení alokací směn.</Alert>;
    }
    if (!outletId) {
      return <Alert severity="warning">Není nastavena provozovna pro zobrazení alokací.</Alert>;
    }

    const anyItems = Object.values(filteredAllocationsByDay).some((items) => items.length > 0);
    if (!anyItems) {
      return (
        <Typography variant="body2" sx={{ color: 'var(--ink-3)' }}>
          Žádné směny pro vybraný týden.
        </Typography>
      );
    }

    return (
      <Box className="glass-week-day-list">
        {weekDays.map((day) => {
          const dayKey = format(day, 'yyyy-MM-dd');
          const dayItems = filteredAllocationsByDay[dayKey] || [];
          const isActiveDay = format(day, 'yyyy-MM-dd') === format(activeDay, 'yyyy-MM-dd');
          const isToday = isSameDay(day, new Date());

          return (
            <Box
              key={dayKey}
              className="glass-day-section"
              data-selected={isActiveDay ? 'true' : 'false'}
              data-today={isToday ? 'true' : 'false'}
              onClick={() => setActiveDay(day)}
              sx={{ cursor: 'pointer' }}
            >
              <Typography className="glass-day-section-title">
                {format(day, 'EEEE d.M.', { locale: cs })}
              </Typography>
              {dayItems.length === 0 && (
                <Typography className="glass-day-empty">Žádné směny.</Typography>
              )}
              {dayItems.map((item) => {
                const employeeName =
                  item?.employee?.fullNameReversed || item?.employee?.fullName || '';
                const isBooked = item?.isBooked;
                const start = item?.startTime ? format(getIsoDate(item.startTime), 'HH:mm') : '';
                const end = item?.endTime ? format(getIsoDate(item.endTime), 'HH:mm') : '';
                const color = item?.colorCode || (isBooked ? item?.bookedColorCode : null);

                return (
                  <Box
                    key={item.id || item._id}
                    className="glass-list-card"
                    sx={{
                      marginBottom: 0,
                      ...(color ? { borderLeft: `12px solid ${color}` } : null),
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="flex-start"
                      justifyContent="space-between"
                      gap={1}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography className="glass-list-card-title" sx={{ mt: 0 }}>
                          {item.name || 'Směna'}
                        </Typography>
                        <Typography className="glass-list-card-meta" sx={{ color: 'var(--ink-2)' }}>
                          {start && end ? `${start} – ${end}` : 'Bez času'}
                        </Typography>

                        {item?.shiftScope?.name && (
                          <Typography className="glass-list-card-meta">
                            {item.shiftScope.name}
                          </Typography>
                        )}

                        {employeeName && (
                          <Typography className="glass-list-card-meta">{employeeName}</Typography>
                        )}

                        <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={
                              item.capacity > 1
                                ? `${item.capacity} místa`
                                : isBooked
                                  ? 'Obsazeno'
                                  : 'Volné'
                            }
                            color={isBooked ? 'success' : 'default'}
                          />
                          {item.isCancellationRequested && (
                            <Chip size="small" color="warning" label="Žádost o zrušení" />
                          )}
                        </Box>
                      </Box>
                      {canUpdateShiftAllocations && (
                        <Box
                          className="glass-allocation-actions"
                          display="flex"
                          alignItems="flex-end"
                        >
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              openAssignDialog(item);
                            }}
                            aria-label="Přiřadit"
                          >
                            <PersonAddAlt fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <>
      <Container maxWidth="sm" sx={{ py: 1.5 }}>
        <MobileWeekChrome
          weekStart={weekStart}
          selectedDay={activeDay}
          onSelectDay={setActiveDay}
          onPreviousWeek={handlePrevWeek}
          onNextWeek={handleNextWeek}
          visibleMonth={visibleMonth}
          availabilities={{}}
          offeredAllocations={{}}
          openAllocations={openAllocationsForCalendar}
          indicatorDateKeys={weekDayKeys}
          onJumpToDay={handleJumpToDay}
        />

        {renderContent()}
      </Container>

      <Snackbar
        open={!!requestStatus}
        autoHideDuration={requestStatus?.duration || 4000}
        onClose={clearRequestStatus}
      >
        {requestStatus && (
          <Alert onClose={clearRequestStatus} severity={requestStatus.severity || 'info'}>
            {requestStatus.message}
          </Alert>
        )}
      </Snackbar>

      <Snackbar
        open={assignSnackbar.open}
        autoHideDuration={3000}
        onClose={() => setAssignSnackbar({ open: false, message: '', severity: 'info' })}
      >
        <Alert
          onClose={() => setAssignSnackbar({ open: false, message: '', severity: 'info' })}
          severity={assignSnackbar.severity || 'info'}
        >
          {assignSnackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={isAssignDialogOpen}
        onClose={closeAssignDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={dialogPaperProps}
        BackdropProps={dialogBackdropProps}
      >
        <DialogTitle sx={{ color: 'var(--ink)' }}>Přiřadit zaměstnance</DialogTitle>
        <DialogContent dividers>
          {isLoadingAssignEmployees && <Alert severity="info">Načítám zaměstnance…</Alert>}
          {!isLoadingAssignEmployees && assignEmployees.length === 0 && (
            <Alert severity="info">Žádní dostupní zaměstnanci.</Alert>
          )}
          {!isLoadingAssignEmployees && assignEmployees.length > 0 && (
            <List dense sx={{ py: 0 }}>
              {assignEmployees.map((employee) => {
                const id = employee._id || employee.id;
                const checked = assignSelectedIds.includes(id);
                return (
                  <ListItem
                    key={id}
                    sx={{
                      mb: 0.5,
                      borderRadius: 1,
                      backgroundColor: getEmployeeRowColor(employee),
                    }}
                  >
                    <Checkbox
                      edge="start"
                      checked={checked}
                      onChange={() => handleAssignToggle(id)}
                      tabIndex={-1}
                    />
                    <Typography variant="body2" noWrap>
                      {employee.fullNameReversed || employee.fullName || ''}
                    </Typography>
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAssignDialog}>Zavřít</Button>
          <Button
            variant="contained"
            onClick={confirmAssignEmployee}
            disabled={isLoadingAssignEmployees}
          >
            Uložit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ShiftAllocations;
